import {
  type StudyMoeCreateDto,
  StudyMoeCreateDtoAggregationMethodEnum,
  StudyMoeCreateDtoBlockingYellowBehaviorEnum,
  StudyMoeCreateDtoPendingMopBehaviorEnum,
  StudyMoeCreateDtoYellowDispositionEnum,
  type StudyMoeSelectDto,
  type StudyMoeUpdateDto,
} from '@@types/api.types';
import type { MeiaMoeEntryFormValues, MeiaSwimlaneMosEntryFormValues } from '@schemas/meia.schema';
import {
  toCreateMoeAggregation,
  toCreateMoeBlocking,
  toCreateMoePending,
  toCreateMoeYellow,
  toUpdateMoeAggregation,
  toUpdateMoeBlocking,
  toUpdateMoePending,
  toUpdateMoeYellow,
} from '@utils/meia-enum.util';

export const aggregationOptions = [
  { label: 'Hybrid', value: StudyMoeCreateDtoAggregationMethodEnum.HYBRID },
  { label: 'Weighted contributing', value: StudyMoeCreateDtoAggregationMethodEnum.WEIGHTED_CONTRIBUTING },
  { label: 'Worst-case blocking', value: StudyMoeCreateDtoAggregationMethodEnum.WORST_CASE_BLOCKING },
];

export const blockingYellowOptions = [
  { label: 'Ignore', value: StudyMoeCreateDtoBlockingYellowBehaviorEnum.IGNORE },
  { label: 'Propagate yellow', value: StudyMoeCreateDtoBlockingYellowBehaviorEnum.PROPAGATE_YELLOW },
];

export const pendingMopOptions = [
  { label: 'Block scoring', value: StudyMoeCreateDtoPendingMopBehaviorEnum.BLOCK_SCORING },
  { label: 'Exclude from average', value: StudyMoeCreateDtoPendingMopBehaviorEnum.EXCLUDE_FROM_AVERAGE },
  { label: 'Treat as red', value: StudyMoeCreateDtoPendingMopBehaviorEnum.TREAT_AS_RED },
  { label: 'Treat as yellow', value: StudyMoeCreateDtoPendingMopBehaviorEnum.TREAT_AS_YELLOW },
];

export const yellowDispositionOptions = [
  { label: 'Accept', value: StudyMoeCreateDtoYellowDispositionEnum.ACCEPT },
  { label: 'Accept with override', value: StudyMoeCreateDtoYellowDispositionEnum.ACCEPT_WITH_OVERRIDE },
  { label: 'Reject', value: StudyMoeCreateDtoYellowDispositionEnum.REJECT },
];

export const ADVANCED_SCORING_TOOLTIPS = {
  aggregationMethod: 'Controls how MoP under this MoE combine into one result.',
  blockingYellowBehavior: 'Controls whether a yellow result from a blocking Task should affect the MoE score.',
  pendingMopBehavior: 'Controls how scoring behaves when a required MoP does not have a result yet.',
  weight: 'Sets how much this MoE contributes to the MoS. Leave empty to distribute weight automatically.',
  yellowDisposition: 'Controls how a yellow MoE result should be treated when scoring the MoS.',
};

export const generateMeiaLocalId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const buildEmptyMoe = (): MeiaMoeEntryFormValues => ({
  _localId: generateMeiaLocalId(),
  aggregationMethod: StudyMoeCreateDtoAggregationMethodEnum.WEIGHTED_CONTRIBUTING,
  blockingYellowBehavior: StudyMoeCreateDtoBlockingYellowBehaviorEnum.IGNORE,
  description: '',
  moeTemplateCode: null,
  pendingMopBehavior: StudyMoeCreateDtoPendingMopBehaviorEnum.TREAT_AS_YELLOW,
  // Leave weight blank so the backend auto-distributes after the entry is created.
  weight: null,
  yellowDisposition: StudyMoeCreateDtoYellowDispositionEnum.ACCEPT,
});

export const buildEmptyMos = (): MeiaSwimlaneMosEntryFormValues => ({
  _localId: generateMeiaLocalId(),
  description: '',
  moeEntries: [],
  mosTemplateCode: null,
  removedMoeIds: [],
});

export const moeFromServer = (moe: StudyMoeSelectDto): MeiaMoeEntryFormValues => ({
  _localId: generateMeiaLocalId(),
  aggregationMethod: toCreateMoeAggregation(moe.aggregationMethod),
  blockingYellowBehavior: toCreateMoeBlocking(moe.blockingYellowBehavior),
  description: moe.description,
  id: moe.id,
  moeTemplateCode: null,
  pendingMopBehavior: toCreateMoePending(moe.pendingMopBehavior),
  weight: moe.weightIsAuto ? null : moe.weight,
  yellowDisposition: toCreateMoeYellow(moe.yellowDisposition),
});

type PersistMoeTreeParams = {
  createMoe: (vars: { mosId: string } & StudyMoeCreateDto) => Promise<StudyMoeSelectDto>;
  deleteMoe: (vars: { moeId: string; mosId: string }) => Promise<void>;
  moeEntries: MeiaMoeEntryFormValues[];
  mosId: string;
  /** Lets the host write the created id back into its form state so a failed pass retries idempotently. */
  onMoeCreated?: (params: { created: StudyMoeSelectDto; moeIndex: number }) => void;
  removedMoeIds: string[];
  updateMoe: (vars: { moeId: string; mosId: string } & StudyMoeUpdateDto) => Promise<StudyMoeSelectDto>;
};

/**
 * Persists one MoS's MoE children: removals first, then per-entry update/create.
 * Shared by the single-MoS modal form and the inline swimlane measures form so the
 * payload mapping stays defined once.
 */
export const persistMoeTree = async (params: PersistMoeTreeParams): Promise<void> => {
  const { createMoe, deleteMoe, moeEntries, mosId, onMoeCreated, removedMoeIds, updateMoe } = params;

  for (const removedMoeId of removedMoeIds) {
    await deleteMoe({ moeId: removedMoeId, mosId });
  }

  for (const [moeIndex, moeEntry] of moeEntries.entries()) {
    if (moeEntry.id) {
      await updateMoe({
        aggregationMethod: toUpdateMoeAggregation(moeEntry.aggregationMethod),
        blockingYellowBehavior: toUpdateMoeBlocking(moeEntry.blockingYellowBehavior),
        description: moeEntry.description,
        moeId: moeEntry.id,
        mosId,
        pendingMopBehavior: toUpdateMoePending(moeEntry.pendingMopBehavior),
        weight: moeEntry.weight,
        yellowDisposition: toUpdateMoeYellow(moeEntry.yellowDisposition),
      });
    } else {
      const created = await createMoe({
        aggregationMethod: moeEntry.aggregationMethod,
        blockingYellowBehavior: moeEntry.blockingYellowBehavior,
        description: moeEntry.description,
        mosId,
        pendingMopBehavior: moeEntry.pendingMopBehavior,
        weight: moeEntry.weight,
        yellowDisposition: moeEntry.yellowDisposition,
      });
      onMoeCreated?.({ created, moeIndex });
    }
  }
};
